import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, Mail, Phone, FileText, Loader2, User, MapPin, Calendar, Clock, Info, CheckSquare } from 'lucide-react';
import Header from "../../components/Public/ui/Header";
import Footer from "../../components/Public/ui/Footer";
import DemoToolWidget from "../../components/DemoToolWidget";
import NeedAssistanceBanner from "../../components/common/NeedAssistanceBanner";

export default function StatusApproved() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [applicationData, setApplicationData] = useState(null);
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/user`, { credentials: "include" });
                const result = await response.json();
                if (result.success && result.status === "approved_no_renew") {
                    setApplicationData(result.applicationData);
                    setUserData(result.userData);
                } else {
                    navigate("/passport/application", { replace: true });
                }
            } catch (err) { console.error(err); } finally { setLoading(false); }
        };
        fetchUserDetails();
    }, [navigate]);

    if (loading) return <div className="min-h-screen bg-slate-50"><Header /><Footer /></div>;

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
            <Header />

            <main className="flex-grow flex items-center justify-center p-4 sm:p-6"
                style={{
                    backgroundImage: "radial-gradient(circle at center, rgba(255,255,255,0.9) 0%, rgba(241,245,249,1) 100%), url('/assets/Images/SLUDI-background.png')",
                    backgroundSize: "cover",
                    backgroundAttachment: "fixed"
                }}>

                <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">

                    {/* Success Header */}
                    <div className="bg-emerald-50/50 border-b border-emerald-100 p-6 sm:px-8">
                        <div className="flex items-center gap-5">
                            <div className="bg-emerald-600 p-3 rounded-xl shadow-md">
                                <CheckCircle className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 leading-tight">Passport ready for collection</h2>
                                <p className="text-slate-500 text-sm mt-0.5">Your application has been approved and the document is being printed.</p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 sm:p-8 space-y-6">

                        {/* 1. Summary Grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <SummaryItem label="Ref number" value={applicationData?.application_number} isMono />
                            <SummaryItem label="Service" value={applicationData?.service_type} />
                            <SummaryItem label="Issued date" value={applicationData?.issue_date ? new Date(applicationData.issue_date).toLocaleDateString() : 'Today'} />
                            <SummaryItem label="Expiry" value={applicationData?.expiry_date ? new Date(applicationData.expiry_date).toLocaleDateString() : '10 Years'} />
                        </div>

                        {/* 2. Enhanced Collection Section */}
                        <section className="grid grid-cols-1 md:grid-cols-5 gap-6 border border-indigo-100 bg-indigo-50/30 rounded-xl overflow-hidden">
                            {/* Left Side: Where & When */}
                            <div className="md:col-span-2 p-5 bg-indigo-50/50 border-r border-indigo-100">
                                <h3 className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <MapPin size={14} /> Pickup location
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm font-bold text-slate-800">Department of Immigration</p>
                                        <p className="text-xs text-slate-600 leading-relaxed">
                                            Sri Subhuthipura Road,<br />Battaramulla, Sri Lanka.
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3 text-slate-600">
                                        <Clock size={14} className="text-indigo-500" />
                                        <p className="text-xs">Mon - Fri: 9:00 AM - 4:00 PM</p>
                                    </div>
                                    <div className="flex items-center gap-3 text-slate-600">
                                        <Calendar size={14} className="text-indigo-500" />
                                        <p className="text-xs">Collect by: <span className="font-bold">{new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toLocaleDateString()}</span></p>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: What to Bring */}
                            <div className="md:col-span-3 p-5">
                                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <CheckSquare size={14} /> Requirements for collection
                                </h3>
                                <ul className="space-y-2.5">
                                    <RequirementItem text="Original National Identity Card (NIC)" />
                                    <RequirementItem text="Previous Passport (if this is a renewal)" />
                                    <RequirementItem text="Digital/Printed Application Receipt" />
                                    <RequirementItem text="Authorization letter (if collecting for someone else)" isOptional />
                                </ul>
                            </div>
                        </section>

                        {/* 3. Your Information */}
                        <section className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                <User size={14} /> Your information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
                                <DataField label="Full name" value={userData?.name} />
                                <DataField label="sludi number" value={userData?.sludiNumber} />
                                <DataField label="Profession" value={applicationData?.profession} />
                                <DataField label="District" value={applicationData?.district} />
                                <DataField label="Email" value={userData?.email} />
                                <DataField label="Mobile" value={userData?.phone} />
                            </div>
                        </section>

                        {/* 4. Help Footer */}
                        <NeedAssistanceBanner 
                            type="success" 
                            message="Contact support if any details on this page are incorrect."
                            email="support@gov.lk"
                        />
                    </div>
                </div>
            </main>
            <Footer />
            <DemoToolWidget context="approved" applicationData={applicationData} />
        </div>
    );
}

/* Internal Components */
function SummaryItem({ label, value, isMono = false }) {
    return (
        <div className="flex flex-col border-r border-slate-100 last:border-0">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-tight">{label}</span>
            <span className={`text-slate-800 font-bold leading-tight truncate ${isMono ? 'font-mono text-sm' : 'text-sm'}`}>
                {value || '—'}
            </span>
        </div>
    );
}

function RequirementItem({ text, isOptional = false }) {
    return (
        <li className="flex items-start gap-2.5 group">
            <div className="mt-0.5 min-w-[14px]">
                <CheckCircle size={14} className="text-emerald-500" />
            </div>
            <span className="text-xs text-slate-700 font-medium">
                {text} {isOptional && <span className="text-slate-400 font-normal italic">(optional)</span>}
            </span>
        </li>
    );
}

function DataField({ label, value }) {
    if (!value) return null;
    return (
        <div className="flex items-center py-1 border-b border-slate-200/40 last:border-0 gap-4">
            <span className="text-[10px] font-medium text-slate-500 uppercase w-32 shrink-0">{label}</span>
            <span className="text-slate-800 font-semibold text-xs">{value}</span>
        </div>
    );
}